import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Controller, Ref, useFormContext } from 'react-hook-form';
import {
  Chip,
  makeStyles,
  TextareaAutosize,
  TextField
} from '@material-ui/core';
import { InfoOutlined } from '@material-ui/icons';
import {
  CapaPcbServiceType,
  CapaQuotationServiceType
} from '@generated/graphql';
import useQueryString from '@hooks/useQueryString';
import { RootState } from '@modules/index';
import { colorSet } from '@components/Provider';
import { CustomizedTypography } from '@components/Customized';
import FormInputLayout from '../Layout/FormInputLayout';
import { RequestQueryString } from '../types';
import { RequestInputCommonProps } from './types';
import HowToModal from '@routes/RequestForQuotation/Dialogs/HowtoModal';

const DetailedRequestInput = React.forwardRef<Ref, RequestInputCommonProps>(
  ({ error }, ref) => {
    const [scenarioDialogOpen, setScenarioDialogOpen] = useState<boolean>(
      false
    );
    const detailedRequestDefaultValue = useSelector<RootState, string>(
      (state) => state.request.detailedRequest ?? ''
    );
    const { detailedRequestInput, secenario } = useStyles();
    const {
      query: { serviceType, process }
    } = useQueryString<RequestQueryString>();
    const selectedProccess = process?.split(',') ?? [];
    const hasPcbDesign = selectedProccess.includes(
      CapaPcbServiceType.CircuitDesign
    );
    const isDesign = serviceType === CapaQuotationServiceType.Design;
    const { control } = useFormContext();

    return (
      <FormInputLayout
        className="detailedRequest"
        label="상세 요청사항"
        required={hasPcbDesign || isDesign}
        description={
          hasPcbDesign ? '제작 목적 및 동작 시나리오를 서술해주세요.' : ''
        }
        extraLabel={
          hasPcbDesign || isDesign ? (
            <>
              <HowToModal
                open={scenarioDialogOpen}
                onClose={() => setScenarioDialogOpen(false)}
                type={serviceType}
              />
              <Chip
                onClick={() => setScenarioDialogOpen(true)}
                className={secenario}
                icon={<InfoOutlined fontSize="small" />}
                label={
                  <CustomizedTypography variant="body2" color="textPrimary">
                    정확한 견적을 받는 법
                  </CustomizedTypography>
                }
              />
            </>
          ) : undefined
        }
      >
        <Controller
          name="detailedRequest"
          control={control}
          defaultValue={detailedRequestDefaultValue}
          render={({ onChange, value }) => (
            <TextField
              data-cy="rfq-detailedRequest"
              className={detailedRequestInput}
              autoComplete="off"
              InputProps={{
                inputComponent: TextareaAutosize,
                value
              }}
              onChange={(e) => onChange(e.target.value.trimStart())}
              variant="outlined"
              error={!!error}
              helperText={error}
              minRows={5}
              maxRows={5}
              fullWidth
              placeholder={
                hasPcbDesign
                  ? `목적 예시 : 진공관 시계를 만들고 싶습니다.
              동작 시나리오 예시 : 버튼 1번 클릭시 작동
              스펙 예시 : 12V 순환모터 [워터펌프814A]`
                  : '제작 목적 및 동작 시나리오를 서술해주세요.'
              }
              multiline
            />
          )}
        />
      </FormInputLayout>
    );
  }
);

export default DetailedRequestInput;

const useStyles = makeStyles((theme) => ({
  detailedRequestInput: {
    '& input': {
      padding: 12
    },
    '& .MuiInputBase-root': {
      alignItems: 'flex-start'
    },
    '& .MuiOutlinedInput-multiline': {
      // height: 120,
      padding: 12,
      borderRadius: 8
    },
    '& ::placeholder': {
      color: colorSet.gray500,
      whiteSpace: 'pre-line'
    }
  },
  secenario: {
    padding: '16px 8px',
    backgroundColor: colorSet.gray200,
    marginBottom: 12,
    '& svg': {
      fill: colorSet.gray700
    },
    '&:hover': {
      cursor: 'pointer',
      backgroundColor: colorSet.gray300
    },
    '&:focus': {
      backgroundColor: colorSet.gray200
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%'
    }
  }
}));
